import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

export default function ManagedCare() {
  const [quality, setQuality] = useState({ hedis: [], stars: [] });
  const [hcc, setHCC] = useState([]);
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    api.get("/managed-care/quality").then(r=>setQuality(r.data));
    api.get("/managed-care/hcc").then(r=>setHCC(r.data));
    api.get("/managed-care/contracts").then(r=>setContracts(r.data));
  }, []);

  const hccCols = [
    { key: "patient_name", label: "Patient" },
    { key: "hcc_code", label: "HCC", render: (r)=><span className="font-mono text-brand-primary">{r.hcc_code}</span> },
    { key: "description", label: "Description" },
    { key: "evidence", label: "Evidence" },
  ];

  const contractCols = [
    { key: "payer", label: "Payer" },
    { key: "contract", label: "Contract" },
    { key: "expected", label: "Expected", render: (r)=>`$${r.expected.toLocaleString()}` },
    { key: "actual", label: "Actual", render: (r)=>`$${r.actual.toLocaleString()}` },
    { key: "variance_pct", label: "Variance", render: (r)=>(
      <span className={r.variance_pct < 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%</span>
    )},
  ];

  return (
    <>
      <PageHeader eyebrow="Module 8" title="Managed Care Intelligence"
        subtitle="HEDIS gaps, STARS ratings, HCC coding opportunities and contract performance." testid="managed-care-page" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="HEDIS Quality Measures" testid="hedis">
          <div className="space-y-3">
            {quality.hedis.map(m => (
              <div key={m.measure}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{m.measure}</span>
                  <span className="text-brand-muted">{m.actual}% / {m.target}%</span>
                </div>
                <div className="h-2 bg-brand-bg rounded-full mt-1.5 overflow-hidden">
                  <div className={`h-full ${m.actual>=m.target?"bg-emerald-500":"bg-amber-500"}`} style={{width: `${Math.min(100, m.actual)}%`}}></div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="STARS Ratings" testid="stars">
          <div className="space-y-3">
            {quality.stars.map(s => (
              <div key={s.measure} className="flex items-center justify-between">
                <span className="text-sm">{s.measure}</span>
                <div className="text-amber-500 font-semibold">{"★".repeat(Math.floor(s.rating))}<span className="opacity-30">{"★".repeat(5-Math.floor(s.rating))}</span><span className="ml-2 text-xs text-brand-muted">{s.rating.toFixed(1)}</span></div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="HCC Coding Opportunities" subtitle="Patients with likely undocumented HCCs" testid="hcc-section">
        <DataTable data={hcc} columns={hccCols} searchKeys={["patient_name","hcc_code","description"]} testid="hcc-table" />
      </SectionCard>

      <div className="mt-6">
        <SectionCard title="Contract Performance" testid="contracts">
          <DataTable data={contracts} columns={contractCols} searchKeys={["payer"]} testid="contracts-table" />
        </SectionCard>
      </div>
    </>
  );
}
